import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { clientRequestHeaders } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Ticket {
  id: string;
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  isDemo?: boolean;
  subject: string;
  body: string;
  status: string;
  priority: string;
  assignee: string | null;
  createdAt: string;
  updatedAt: string;
  unreadCount?: number;
}

interface Message {
  id: string;
  authorRole: string;
  body: string;
  createdAt: string;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
}

interface PendingAttachment {
  url: string;
  name: string;
}

const STATUS_LABELS: Record<string, string> = {
  open: "Abierto",
  in_progress: "En curso",
  resolved: "Resuelto",
  closed: "Cerrado",
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "Baja",
  normal: "Normal",
  high: "Alta",
  urgent: "Urgente",
};

function MessageAttachment({ url, name }: { url: string; name?: string | null }) {
  const probe = `${name ?? ""} ${url}`;
  const isImage = /\.(jpe?g|png|webp|gif)(\?|#|$)/i.test(probe);
  const isPdf = /\.pdf(\?|#|$)/i.test(probe);
  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-2">
        <img src={url} alt={name ?? "Adjunto"} className="max-h-40 rounded-md border" />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 inline-flex items-center gap-1 rounded-md border border-border/60 bg-background/80 px-2 py-1 text-xs font-medium text-primary hover:underline"
    >
      <Paperclip className="h-3 w-3 shrink-0" />
      {name ?? (isPdf ? "Abrir PDF" : "Ver adjunto")}
    </a>
  );
}

export function SupportInboxPanel() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyAttachment, setReplyAttachment] = useState<PendingAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const replyFileRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("open_active");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [includeDemos, setIncludeDemos] = useState(false);

  const auth = () => ({ ...clientRequestHeaders(), "Content-Type": "application/json" });

  const loadTickets = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/support", { credentials: "include", headers: auth() });
      if (r.ok) setTickets(((await r.json()) as { tickets?: Ticket[] }).tickets ?? []);
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (id: string) => {
    setSelectedId(id);
    setReplyAttachment(null);
    const r = await fetch(`/api/admin/support?ticket_id=${encodeURIComponent(id)}`, { credentials: "include", headers: auth() });
    if (r.ok) {
      const d = (await r.json()) as { messages?: Message[] };
      setMessages(d.messages ?? []);
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, unreadCount: 0 } : t)));
    }
  };

  useEffect(() => { void loadTickets(); }, []);

  const patchTicket = async (id: string, patch: { status?: string; priority?: string; assignee?: string }) => {
    await fetch(`/api/admin/support?id=${id}`, { method: "PATCH", credentials: "include", headers: auth(), body: JSON.stringify(patch) });
    await loadTickets();
    if (selectedId === id) await loadThread(id);
  };

  const handleFilePick = async (file: File | undefined) => {
    if (!file || !selectedId) return;
    const ticket = tickets.find((t) => t.id === selectedId);
    if (!ticket) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch("/api/support/upload", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(ticket.tenantId) },
        body: form,
      });
      const d = (await r.json()) as { url?: string; name?: string; error?: string };
      if (!r.ok || !d.url) {
        toast.error(d.error ?? "No se pudo subir");
        return;
      }
      setReplyAttachment({ url: d.url, name: d.name ?? file.name });
    } finally {
      setUploading(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    setSending(true);
    try {
      await fetch("/api/admin/support", {
        method: "POST",
        credentials: "include",
        headers: auth(),
        body: JSON.stringify({
          ticketId: selectedId,
          body: reply.trim(),
          attachmentUrl: replyAttachment?.url,
          attachmentName: replyAttachment?.name,
        }),
      });
      setReply("");
      setReplyAttachment(null);
      await loadThread(selectedId);
      await loadTickets();
    } finally {
      setSending(false);
    }
  };

  const kpis = useMemo(() => {
    const real = tickets.filter((t) => includeDemos || !t.isDemo);
    return {
      open: real.filter((t) => t.status === "open").length,
      inProgress: real.filter((t) => t.status === "in_progress").length,
      resolved: real.filter((t) => t.status === "resolved").length,
      closed: real.filter((t) => t.status === "closed").length,
      unassigned: real.filter((t) => !t.assignee && t.status !== "closed").length,
      critical: real.filter((t) => (t.priority === "urgent" || t.priority === "high") && t.status !== "closed").length,
    };
  }, [tickets, includeDemos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (!includeDemos && t.isDemo) return false;
      if (statusFilter === "open_active" && (t.status === "closed" || t.status === "resolved")) return false;
      else if (statusFilter !== "all" && statusFilter !== "open_active" && t.status !== statusFilter) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (q) {
        const hay = `${t.subject} ${t.tenantName ?? ""} ${t.tenantSlug ?? ""} ${t.body}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter, priorityFilter, includeDemos]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const selected = tickets.find((t) => t.id === selectedId);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[
          { label: "Abiertos", value: kpis.open },
          { label: "En curso", value: kpis.inProgress },
          { label: "Resueltos", value: kpis.resolved },
          { label: "Cerrados", value: kpis.closed },
          { label: "Sin asignar", value: kpis.unassigned, warn: kpis.unassigned > 0 },
          { label: "Críticos", value: kpis.critical, warn: kpis.critical > 0 },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border p-2">
            <p className="text-[10px] text-muted-foreground">{k.label}</p>
            <p className={cn("text-lg font-semibold tabular-nums", k.warn && "text-amber-600")}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Input className="h-8 max-w-xs text-xs" placeholder="Buscar asunto, tenant…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="open_active">Abiertos + en curso</option>
          <option value="all">Todos los estados</option>
          <option value="open">Abierto</option>
          <option value="in_progress">En curso</option>
          <option value="resolved">Resuelto</option>
          <option value="closed">Cerrado</option>
        </select>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="all">Toda prioridad</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="normal">Normal</option>
          <option value="low">Baja</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={includeDemos} onChange={(e) => setIncludeDemos(e.target.checked)} />
          Incluir demos/seed
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr] min-h-[420px]">
        <div className="space-y-1 border rounded-lg p-2 max-h-[560px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-2">Sin tickets que coincidan con el filtro.</p>
          ) : (
            filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => void loadThread(t.id)}
                className={cn(
                  "w-full text-left rounded-md p-2 text-sm hover:bg-muted/60",
                  selectedId === t.id && "bg-muted",
                )}
              >
                <div className="flex items-center justify-between gap-1">
                  <p className="font-medium line-clamp-1">{t.subject}</p>
                  {(t.unreadCount ?? 0) > 0 ? (
                    <Badge variant="destructive" className="h-5 min-w-5 px-1 text-[10px]">{t.unreadCount}</Badge>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t.tenantName ?? t.tenantId.slice(0, 8)}
                  {t.isDemo ? " · demo" : ""} · {STATUS_LABELS[t.status] ?? t.status} · {PRIORITY_LABELS[t.priority] ?? t.priority}
                  {t.assignee ? ` · ${t.assignee}` : " · sin asignar"}
                </p>
              </button>
            ))
          )}
        </div>

        <div className="border rounded-lg p-4 flex flex-col min-h-[320px]">
          {!selected ? (
            <p className="text-sm text-muted-foreground">Seleccioná un ticket.</p>
          ) : (
            <>
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{selected.subject}</p>
                    <p className="text-xs text-muted-foreground">
                      {selected.tenantName ?? selected.tenantId} ({selected.tenantSlug ?? "—"})
                      {selected.isDemo ? " · demo/seed" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                    value={selected.status}
                    onChange={(e) => void patchTicket(selected.id, { status: e.target.value })}
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <select
                    className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                    value={selected.priority}
                    onChange={(e) => void patchTicket(selected.id, { priority: e.target.value })}
                  >
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <Input
                    className="h-7 w-40 text-xs"
                    placeholder="Asignar a…"
                    defaultValue={selected.assignee ?? ""}
                    onBlur={(e) => {
                      if (e.target.value !== (selected.assignee ?? "")) void patchTicket(selected.id, { assignee: e.target.value });
                    }}
                  />
                </div>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto mb-3 max-h-[280px]">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-lg p-2 text-sm max-w-[85%]",
                      m.authorRole === "ops" ? "ml-auto bg-primary/10" : "bg-muted/50",
                    )}
                  >
                    <Badge variant="outline" className="text-[10px] mb-1">{m.authorRole}</Badge>
                    <p>{m.body}</p>
                    {m.attachmentUrl ? <MessageAttachment url={m.attachmentUrl} name={m.attachmentName} /> : null}
                    <p className="text-[10px] text-muted-foreground mt-1">{m.createdAt.slice(0, 16)}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Respuesta ops…" rows={2} className="flex-1" />
                  <Button size="sm" disabled={sending} onClick={() => void sendReply()}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <input ref={replyFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => void handleFilePick(e.target.files?.[0])} />
                  <Button type="button" size="sm" variant="ghost" disabled={uploading} onClick={() => replyFileRef.current?.click()}>
                    <Paperclip className="h-4 w-4 mr-1" /> Adjuntar
                  </Button>
                  {replyAttachment ? (
                    <span className="text-xs flex items-center gap-1 text-muted-foreground">
                      {replyAttachment.name}
                      <button type="button" onClick={() => setReplyAttachment(null)} aria-label="Quitar adjunto"><X className="h-3 w-3" /></button>
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
